import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import maplibre, {
  AddLayerObject,
  LngLatLike,
  Map as MlMap,
} from 'maplibre-gl';
import { Feature, GeoJSON } from 'geojson';
import { Frame } from '@/types/location';
import { Coordinate } from '@/types/geojson';
import { SourceOptions } from '@/types/map';
import {
  clearAllDynamicLayersAndSources,
  clearStaticLayersAndSources,
  createLayersAndSources,
  getBounds,
  initializeMap,
} from '@/utils/scout';
import { getMapboxAccessToken } from '@/utils/map';
import ResetIcon from '@/components/ui/icons/ResetIcon';

const MINIMAP_DOTS_LAYER = 'minimap-dots-hm-layer';
const MINIMAP_DOTS_SOURCE = 'minimap-dots-hm-source';

const MINIMAP_ACTIVE_DOTS_LAYER = 'minimap-active-dots-hm-layer';
const MINIMAP_ACTIVE_DOTS_SOURCE = 'minimap-active-dots-hm-source';

export const chevron = `<svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.4835 4.01218C8.39508 0.100594 14.737 0.100594 18.6486 4.01218C22.5602 7.92377 22.5602 14.2657 18.6486 18.1773C14.737 22.0889 8.39508 22.0889 4.4835 18.1773C0.571907 14.2657 0.571907 7.92377 4.4835 4.01218Z" fill="#0A0E14" stroke="black" stroke-width="1.5"/>
<path d="M15.3985 5.31152H10.6333L5.91602 11.1716L10.6333 17.0319H15.3984L10.6811 11.1717L15.3985 5.31152Z" fill="white"/>
</svg>
`;

export const dot = `<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.07045 17.5274C0.354121 13.811 0.354121 7.78568 4.07045 4.06935C7.78677 0.353023 13.8121 0.353023 17.5285 4.06935C21.2448 7.78568 21.2448 13.811 17.5285 17.5274C13.8121 21.2437 7.78677 21.2437 4.07045 17.5274Z" fill="white" stroke="black" stroke-width="2.5"/>
<path d="M7.39903 14.198C5.52147 12.3205 5.52147 9.27635 7.39903 7.39879C9.27659 5.52122 12.3207 5.52122 14.1983 7.39879C16.0758 9.27635 16.0758 12.3205 14.1983 14.198C12.3207 16.0756 9.27659 16.0756 7.39903 14.198Z" fill="#0A0E14" stroke="black" stroke-width="0.72"/>
</svg>`;

export const dotArrow = `<svg width="22" height="22" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g transform="rotate(90 6 6)">
    <path d="M9.9375 5.89074C9.9375 8.09988 8.14664 9.89074 5.9375 9.89074C3.72836 9.89074 1.9375 8.09988 1.9375 5.89074C1.9375 3.6816 3.72836 1.89074 5.9375 1.89074C8.14664 1.89074 9.9375 3.6816 9.9375 5.89074Z" fill="white" />
    <path fill-rule="evenodd" clip-rule="evenodd" d="M11.4375 5.89074C11.4375 8.92831 8.97507 11.3907 5.9375 11.3907C2.89993 11.3907 0.4375 8.92831 0.4375 5.89074C0.4375 2.85317 2.89993 0.390739 5.9375 0.390739C8.97507 0.390739 11.4375 2.85317 11.4375 5.89074ZM5.9375 9.89074C8.14664 9.89074 9.9375 8.09988 9.9375 5.89074C9.9375 3.6816 8.14664 1.89074 5.9375 1.89074C3.72836 1.89074 1.9375 3.6816 1.9375 5.89074C1.9375 8.09988 3.72836 9.89074 5.9375 9.89074Z" fill="black" />
    <path d="M8.26271 2.96064H5.88013L3.52148 5.89069L5.88011 8.82084H8.26269L5.90402 5.89075L8.26271 2.96064Z" fill="#0A0E14" />
  </g>
</svg>`;

const loadSvgImage = async (svg: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
};

interface Props {
  center: number[];
  geojson: GeoJSON;
  sortedSequences: Frame[][] | null;
  activeSequence: Frame[] | null;
  activeSequenceIndex: number;
  activeFrameIndex: { value: number };
  apiCallsComplete: boolean;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  setActiveFrameIndex: Dispatch<
    SetStateAction<{
      value: number;
    }>
  >;
  hasActiveBurst: boolean;
}

const MiniMap: React.FC<Props> = ({
  center,
  geojson,
  sortedSequences,
  activeSequence,
  activeSequenceIndex,
  activeFrameIndex,
  apiCallsComplete,
  setActiveSequenceIndex,
  setActiveFrameIndex,
  hasActiveBurst,
}) => {
  const mapContainer: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MlMap | null>(null);
  const [coordinates, setCoordinates] = useState<[number, number][]>([]);
  const initialized = useRef(false);
  const [lon, lat] = center;

  useEffect(() => {
    // @ts-ignore
    maplibre.accessToken = getMapboxAccessToken();

    if (!initialized.current && mapContainer.current) {
      initialized.current = true;
      const map = initializeMap({
        mapContainer: mapContainer.current,
        onLoadCallback: newMap => setMap(newMap),
        zoom: 13,
        center: center as LngLatLike,
      });

      map.on('load', async () => {
        const arrow = await loadSvgImage(dotArrow);
        map.addImage('dot-arrow', arrow);

        const noArrow = await loadSvgImage(dot);
        map.addImage('dot', noArrow);
      });
    }
  }, [center]);

  useEffect(() => {
    if (lon && lat && map) {
      map.setCenter([lon, lat] as LngLatLike);
      map.setZoom(13);
    }
  }, [lon, lat, map]);

  useEffect(() => {
    if (map) {
      map.keyboard.disable();
    }
  }, [map, hasActiveBurst, geojson]);

  useEffect(() => {
    // Create a layer for all sequences of frames
    if (!map || !sortedSequences || !apiCallsComplete) return;

    clearAllDynamicLayersAndSources(map, 'dynamic');

    const allCoordinates: [number, number][] = [];
    const dots: Feature[] = [];

    sortedSequences.forEach((sequence: Frame[], sequenceIndex: number) => {
      const lines: Coordinate[] = [];

      sequence.forEach((frame: Frame, frameIndex: number) => {
        lines.push([frame.position.lon, frame.position.lat]);
        dots.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [frame.position.lon, frame.position.lat],
          },
          id: `${frame.sequence}_${frame.idx}`,
          properties: {
            sequenceIndex,
            frameIndex,
            bearing: frame.bearing,
          },
        });
      });

      allCoordinates.push(...lines);
    });

    clearStaticLayersAndSources(
      map,
      [MINIMAP_DOTS_LAYER],
      [MINIMAP_DOTS_SOURCE],
    );

    const dotsGeoJSON: GeoJSON = {
      type: 'FeatureCollection',
      features: dots,
    };

    const dotsSourceOptions: SourceOptions[] = [
      {
        id: MINIMAP_DOTS_SOURCE,
        options: {
          type: 'geojson',
          data: dotsGeoJSON,
        },
      },
    ];

    const dotsLayersOptions: AddLayerObject[] = [
      {
        id: MINIMAP_DOTS_LAYER,
        type: 'symbol',
        source: MINIMAP_DOTS_SOURCE,
        layout: {
          'icon-image': ['case', ['has', 'bearing'], 'dot-arrow', 'dot'],
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          // @ts-ignore
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            16,
            0.3,
            18,
            0.5,
            22,
            0.7,
          ],
        },
      },
    ];

    createLayersAndSources(map, dotsLayersOptions, dotsSourceOptions);

    map.on('click', MINIMAP_DOTS_LAYER, (e: any) => {
      const { sequenceIndex, frameIndex } = e.features[0].properties;
      setActiveSequenceIndex(sequenceIndex);
      setActiveFrameIndex({ value: frameIndex });
    });

    if (allCoordinates.length > 0) {
      map.fitBounds(getBounds(allCoordinates), {
        padding: 60, // Optional padding around the bounds
        duration: 500,
      });
    }

    setCoordinates(allCoordinates);
  }, [
    map,
    sortedSequences,
    apiCallsComplete,
    setActiveSequenceIndex,
    setActiveFrameIndex,
  ]);

  useEffect(() => {
    // Create a layer for the active sequence of frames
    if (!map || !activeSequence || !apiCallsComplete) return;

    clearStaticLayersAndSources(
      map,
      [MINIMAP_ACTIVE_DOTS_LAYER],
      [MINIMAP_ACTIVE_DOTS_SOURCE],
    );

    const activeDots: Feature[] = activeSequence.map((frame: Frame) => {
      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [frame.position.lon, frame.position.lat],
        },
        id: `${frame.sequence}_${frame.idx}`,
        properties: {
          bearing: frame.bearing,
        },
      };
    });

    const dotsGeoJSON: GeoJSON = {
      type: 'FeatureCollection',
      features: activeDots,
    };

    const activeSourceOptions: SourceOptions[] = [
      {
        id: MINIMAP_ACTIVE_DOTS_SOURCE,
        options: {
          type: 'geojson',
          data: dotsGeoJSON,
        },
      },
    ];

    const activeLayersOptions: AddLayerObject[] = [
      {
        id: MINIMAP_ACTIVE_DOTS_LAYER,
        type: 'symbol',
        source: MINIMAP_ACTIVE_DOTS_SOURCE,
        layout: {
          'icon-image': ['case', ['has', 'bearing'], 'dot-arrow', 'dot'],
          'icon-rotate': ['get', 'bearing'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          // @ts-ignore
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            16,
            0.6,
            18,
            0.8,
            22,
            1,
          ],
        },
      },
    ];

    createLayersAndSources(map, activeLayersOptions, activeSourceOptions);
  }, [map, apiCallsComplete, activeSequence, activeSequenceIndex]);

  useEffect(() => {
    // Create a layer for the active frame
    if (!map || !activeSequence || !apiCallsComplete) return;

    const activeFrame = activeSequence[activeFrameIndex.value];
    const { lon, lat } = activeFrame.position;

    const markerEl = document.createElement('div');
    markerEl.style.display = 'flex';

    if (activeFrame.bearing) {
      markerEl.innerHTML = chevron;
    } else {
      markerEl.innerHTML = dot;
    }

    const marker = new maplibre.Marker({
      element: markerEl,
      draggable: false,
    });
    marker.setLngLat([lon, lat]).addTo(map);

    if (activeFrame.bearing) {
      marker.setRotation(activeFrame.bearing + 90 - map.getBearing());
    }

    map.on('move', () => {
      if (activeFrame.bearing) {
        marker.setRotation(activeFrame.bearing + 90 - map.getBearing());
      }
    });

    return () => {
      if (marker) {
        marker.remove();
      }
    };
  }, [map, apiCallsComplete, activeSequence, activeFrameIndex]);

  return (
    <div
      ref={mapContainer}
      id="scout-map"
      className="relative size-full overflow-hidden rounded-md">
      <div
        className="absolute right-4 top-4 z-10 cursor-pointer"
        onClick={() => {
          if (map && coordinates.length > 0) {
            map.fitBounds(getBounds(coordinates), {
              padding: 60,
            });
          }
        }}>
        {map && <ResetIcon className="size-6 fill-neutral-0" />}
      </div>
    </div>
  );
};

export default MiniMap;
